import React, { useEffect, useRef, useState } from "react";
import { Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useSettings } from "../context/SettingsContext";

const BITRIX_I18N = {
  en: {
    "ilk adı": "First Name",
    "adınız": "First Name",
    "soyadı": "Last Name",
    "telefon": "Phone",
    "e-posta": "Email",
    "bütçeniz nedir ? (budget)": "What is your budget?",
    "500bin-2milyon ya da 5milyon-10milyon arası diyebilirsiniz.": "For example 500K–2M or 5M–10M.",
    "hangi şehirde oturuyorsunuz ?": "Which city do you live in?",
    "sunum ya da seminer aldınız mı ?": "Have you attended a presentation or seminar?",
    "mesajınız": "Your message",
    "gönder": "Send",
    "tekrar gönder": "Send again",
    "teşekkürler!": "Thank you!",
    "formunuz gönderildi": "Your form has been sent",
    "bir hata oluştu": "Something went wrong",
  },
  ar: {
    "ilk adı": "الاسم الأول",
    "adınız": "الاسم الأول",
    "soyadı": "اسم العائلة",
    "telefon": "رقم الهاتف",
    "e-posta": "البريد الإلكتروني",
    "bütçeniz nedir ? (budget)": "ما هي ميزانيتك؟",
    "500bin-2milyon ya da 5milyon-10milyon arası diyebilirsiniz.": "مثال: 500 ألف – 2 مليون أو 5 – 10 مليون.",
    "hangi şehirde oturuyorsunuz ?": "في أي مدينة تسكن؟",
    "sunum ya da seminer aldınız mı ?": "هل حضرت عرضاً أو ندوة؟",
    "mesajınız": "رسالتك",
    "gönder": "إرسال",
    "tekrar gönder": "إرسال مرة أخرى",
    "teşekkürler!": "شكراً لك!",
    "formunuz gönderildi": "تم إرسال النموذج",
    "bir hata oluştu": "حدث خطأ ما",
  },
};

const normalizeKey = (text) =>
  text
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\*+$/, "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const NORMALIZED_I18N = Object.fromEntries(
  Object.entries(BITRIX_I18N).map(([lang, dict]) => [
    lang,
    Object.fromEntries(Object.entries(dict).map(([k, v]) => [normalizeKey(k), v])),
  ])
);

// Bitrix24 forms are authored in Turkish; translate their visible labels in EN/AR.
let remoteDict = {};
let remoteLoaded = null;
const loadRemoteDict = () => {
  if (!remoteLoaded) {
    remoteLoaded = api.get("/form-translations").then((r) => { remoteDict = r.data || {}; }).catch(() => {});
  }
  return remoteLoaded;
};

const lookup = (key, lang) =>
  (remoteDict[key] ? remoteDict[key][lang] : "") || NORMALIZED_I18N[lang]?.[key] || "";

const localizeBitrix = (root, lang) => {
  if (!root || !NORMALIZED_I18N[lang]) return [];
  const missing = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  nodes.forEach((node) => {
    const raw = node.nodeValue || "";
    const key = normalizeKey(raw);
    if (!key) return;
    const translated = lookup(key, lang);
    if (translated) node.nodeValue = raw.includes("*") ? `${translated} ` : translated;
    else if (/[çğıöşüÇĞİÖŞÜ]/.test(raw) && raw.trim().length > 2) missing.push(raw.trim());
  });
  root.querySelectorAll("[placeholder]").forEach((el) => {
    const translated = lookup(normalizeKey(el.getAttribute("placeholder") || ""), lang);
    if (translated) el.setAttribute("placeholder", translated);
  });
  return missing;
};

// Injects a raw Bitrix24 embed code string so its <script> executes.
const EmbedForm = ({ code, lang }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !code) return;
    el.innerHTML = "";
    const temp = document.createElement("div");
    temp.innerHTML = code;
    Array.from(temp.childNodes).forEach((node) => {
      if (node.tagName === "SCRIPT") {
        const s = document.createElement("script");
        Array.from(node.attributes).forEach((a) => s.setAttribute(a.name, a.value));
        if (node.src) s.src = node.src;
        else s.text = node.textContent;
        el.appendChild(s);
      } else {
        el.appendChild(node.cloneNode(true));
      }
    });
    const apply = () => localizeBitrix(el, lang);
    let requested = false;
    const run = () => {
      const missing = apply();
      if (missing.length && !requested) {
        requested = true;
        api.post("/form-translations/auto", { texts: missing.slice(0, 12) })
          .then((r) => { remoteDict = { ...remoteDict, ...(r.data || {}) }; apply(); })
          .catch(() => {});
      }
    };
    const observer = new MutationObserver(run);
    observer.observe(el, { childList: true, subtree: true, characterData: true });
    loadRemoteDict().then(run);
    return () => observer.disconnect();
  }, [code, lang]);
  return <div ref={ref} data-testid="bitrix-embed" className="min-h-[320px]" />;
};

const NativeForm = () => {
  const { t } = useLang();
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "", type: "callback" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", f);
      toast.success(t.contact.success);
      setF({ name: "", email: "", phone: "", message: "", type: "callback" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t.contact.error);
    } finally {
      setBusy(false);
    }
  };
  const line = "w-full bg-white border border-green/20 rounded-2xl px-5 py-3.5 outline-none text-charcoal placeholder:text-charcoal/40 focus:border-green focus:ring-4 focus:ring-gold/25 transition-all";
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="callback-native-form">
      <input required value={f.name} onChange={set("name")} placeholder={`${t.contact.name} *`} data-testid="cb-name" className={line} />
      <input required dir="ltr" type="tel" value={f.phone} onChange={set("phone")} placeholder={`${t.contact.phone} *`} data-testid="cb-phone" className={line} />
      <input required dir="ltr" type="email" value={f.email} onChange={set("email")} placeholder={`${t.contact.email} *`} data-testid="cb-email" className={line} />
      <textarea rows={3} value={f.message} onChange={set("message")} placeholder={t.contact.message} data-testid="cb-message" className={`${line} resize-none`} />
      <button type="submit" disabled={busy} data-testid="cb-submit" className="btn-shine w-full rounded-full bg-green text-white font-semibold py-4 hover:bg-green-deep transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
        {busy ? "…" : t.cta.send} <Send className="w-4 h-4" />
      </button>
    </form>
  );
};

/** Brand-skinned Bitrix24 form with a native fallback when no embed code is set. */
export const BrandedForm = ({ className = "" }) => {
  const { tc, lang } = useLang();
  const { settings } = useSettings();
  const raw = settings.bitrix_form_code;
  // Use the language-specific embed when set, otherwise reuse any configured one
  // so every language shows the same Bitrix24 form.
  const code = (tc(raw) || "").trim()
    || (typeof raw === "object" && raw ? [raw.tr, raw.en, raw.ar].map((v) => (v || "").trim()).find(Boolean) : "")
    || "";
  return (
    <div className={`bitrix-skin ${className}`} data-testid="branded-form">
      {code ? <EmbedForm code={code} lang={lang} /> : <NativeForm />}
    </div>
  );
};

export const FormPhoneLink = () => {
  const { settings } = useSettings();
  if (!settings.phone) return null;
  return (
    <a href={`tel:${settings.phone}`} data-testid="callback-phone-link" className="flex items-center justify-center gap-2 text-green font-display text-2xl">
      <Phone className="w-5 h-5" /> <span dir="ltr" className="[unicode-bidi:plaintext]">{settings.phone}</span>
    </a>
  );
};
